import {formatDate, formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbAlpha, adbCategoryAxis, adbChartTheme, adbGrid, adbLegend, adbRedrawOnThemeChange, adbTooltip, adbValueAxis} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {AdminDashboardQueryParams, AdminDashboardWidgetState} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardSeasonalLoad} from '@features/admin-panel/models/admin-dashboard-seasonal.models';
import {AdminDashboardReportBlock, AdminDashboardReportProvider} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const seasonalLoadCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const day = (value: string | Date): string => formatDate(value, 'd MMM', 'en-US', 'UTC');

const has = (value: number | null | undefined): value is number => value !== null && value !== undefined;

/**
 * Projects, scenarios & entities "Seasonal load" (ITP-E-218-02, Plan ITP-P-50 Q19): scenarios created per ISO week,
 * this year against last year, and the next 12 weeks projected, naming the busiest week ahead. Empty — so the
 * dashboard hides it — until the server has 64 weeks of usage history. Reloads whenever the dashboard's params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-seasonal-load',
  templateUrl: './admin-dashboard-seasonal-load.component.html',
  styleUrls: ['./admin-dashboard-seasonal-load.component.scss']
})
export class AdminDashboardSeasonalLoadComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly title = 'Seasonal load';
  readonly description = 'Scenarios created per week, this year against last year, and the next 12 weeks.';
  readonly failedTitle = 'Couldn\'t load seasonal load';

  data: AdminDashboardSeasonalLoad | null = null;
  /** False until the first response, so the empty state never flashes. */
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('platform-usage', this.host.nativeElement, this);
  }

  /** The chart container exists only while the card is available. */
  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = seasonalLoadCharts.init(this.chartElement);
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** The server has enough history and sent the weeks. An absent or older response counts as not available. */
  get available(): boolean {
    return this.data?.available === true && (this.data.weeks?.length ?? 0) > 0;
  }

  /** "Busiest week ahead: w/c 19 Oct (~1,235 scenarios)"; null without a peak. */
  get peakText(): string | null {
    const peakWeek = this.data?.peakWeek;
    const peakValue = this.data?.peakValue;
    if (!this.available || !peakWeek || !has(peakValue)) {
      return null;
    }
    return `Busiest week ahead: w/c ${day(peakWeek)} (~${countOf(Math.round(peakValue), 'scenario')})`;
  }

  /** "Before 3 Oct: based on items that still exist" — the day after the last backfilled day; null without backfill. */
  get backfillNote(): string | null {
    const until = this.data?.backfilledUntil;
    if (!this.available || !until) {
      return null;
    }
    return `Before ${day(new Date(Date.parse(until) + ONE_DAY_MS))}: based on items that still exist`;
  }

  /** "w/c 12 Oct 2026<br/>Last year: 9 scenarios<br/>Projected: ~11 scenarios" — one line per value the week has. */
  tooltipText(index: number): string {
    const week = this.data?.weeks?.[index];
    if (!week) {
      return '';
    }
    const lines = [`w/c ${formatDate(week.weekStart, 'd MMM yyyy', 'en-US', 'UTC')}`];
    if (has(week.thisYear)) {
      lines.push(`This year: ${countOf(week.thisYear, 'scenario')}`);
    }
    if (has(week.lastYear)) {
      lines.push(`Last year: ${countOf(week.lastYear, 'scenario')}`);
    }
    if (has(week.projected)) {
      lines.push(`Projected: ~${countOf(Math.round(week.projected), 'scenario')}`);
    }
    return lines.join('<br/>');
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-09). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: this.title, text: this.failedTitle}];
    }
    if (!this.available) {
      return [];
    }
    const block: AdminDashboardReportBlock = {
      kind: 'chart',
      title: this.title,
      subtitle: this.peakText ?? this.description,
      image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
      widthPx: this.chart?.getWidth() ?? 0,
      heightPx: this.chart?.getHeight() ?? 0
    };
    const note = this.backfillNote;
    if (note) {
      block.note = note;
    }
    return [block];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardSeasonalLoad>(
      'AdminDashboardSeasonalLoadQuery', this.params, 'Seasonal'
    ).subscribe({
      next: data => this.show(data ?? null, false),
      error: () => this.show(null, true)
    });
  }

  private show(data: AdminDashboardSeasonalLoad | null, failed: boolean): void {
    this.data = data;
    this.failed = failed;
    this.loaded = true;
    this.render();
    this.report(failed ? 'failed' : this.available ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  private render(): void {
    if (!this.chart || !this.available) {
      return;
    }
    const weeks = this.data.weeks;
    // Three lines over the same weeks: this year solid blue, last year dashed ink 2, the projection dashed faded
    // blue. Colours come from the card's --adb-* tokens.
    const t = adbChartTheme(this.host.nativeElement);
    const projectedColor = adbAlpha(t.blue, 0.5);
    const option = {
      textStyle: {fontFamily: t.font},
      grid: adbGrid(),
      legend: {...adbLegend(t), data: ['This year', 'Last year', 'Projected']},
      xAxis: adbCategoryAxis(t, weeks.map(w => day(w.weekStart)), {boundaryGap: false, axisLabel: {color: t.ink2, fontSize: 10, interval: 'auto'}}),
      yAxis: adbValueAxis(t, (value: number) => formatNumber(value, 'en-US', '1.0-0'), {minInterval: 1}),
      tooltip: adbTooltip(t, {
        formatter: (points: any) => this.tooltipText((Array.isArray(points) ? points[0] : points)?.dataIndex ?? 0)
      }),
      series: [
        {
          name: 'This year', type: 'line', data: weeks.map(w => w.thisYear ?? null), showSymbol: false,
          lineStyle: {width: 2, color: t.blue}, itemStyle: {color: t.blue}
        },
        {
          name: 'Last year', type: 'line', data: weeks.map(w => w.lastYear ?? null), showSymbol: false,
          lineStyle: {width: 1.25, type: 'dashed', color: t.ink2}, itemStyle: {color: t.ink2}
        },
        {
          name: 'Projected', type: 'line', data: weeks.map(w => w.projected ?? null), showSymbol: false,
          lineStyle: {width: 2, type: 'dashed', color: projectedColor}, itemStyle: {color: projectedColor}
        }
      ]
    };
    this.chart.setOption(option, true);
  }
}
